import { Skeleton } from "@medusajs/ui"
import { useState } from "react"
import { useStoreTz } from "@/analytics/pickers"
import { ErrorState } from "@/components/ui"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { cachedPrefs, DEFAULT_PREFS, useHome, useHomePrefs, useSaveHomePrefs, type HomePrefs, type MetricKey } from "@/home/api"
import { AdCampaignsPanel } from "@/home/ad-campaigns"
import { greeting, todayEyebrow } from "@/home/format"
import { Acquisition, Attention, Funnel, PaymentsHealth, PrimaryChart, RecentOrders, Subscriptions, Waterfall } from "@/home/panels"
import { ControlBar, KpiStrip, TilePicker } from "@/home/top"

/**
 * Keel Home (Keel Home Build Spec v1): are we on pace, where is the money going, what needs action.
 * One request builds every section (GET /admin/terify/dashboard/home); the date range, comparison,
 * KPI tiles, chart metric and MID tab are saved per staff member (GET/POST /admin/terify/me/home).
 * Order on phones and tablets: controls, KPIs, Needs attention, then the rest.
 */
export function HomePage() {
  const me = useCurrentMe()
  const tz = useStoreTz()
  const can = useCan()
  const prefsQ = useHomePrefs()
  const saveM = useSaveHomePrefs()
  const prefs: HomePrefs = prefsQ.data ?? cachedPrefs() ?? DEFAULT_PREFS
  const save = (p: Partial<HomePrefs>) => saveM.mutate(p)
  const live = prefs.range === "today" || prefs.range === "mtd" || ["7d", "30d", "90d"].includes(prefs.range)
  const allowed = can("analytics:read")
  const { data, error, isFetching } = useHome(allowed ? prefs : null, live)
  const [editTiles, setEditTiles] = useState(false)
  const chart: MetricKey = data?.definitions[prefs.chart]?.series === false ? "net_sales" : prefs.chart
  const name = me.user.first_name

  return (
    <div className="flex min-w-0 flex-col gap-4 pb-8" data-testid="home">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="k-eyebrow">{todayEyebrow(tz)}</div>
          <h1 className="k-h1 mt-1">{greeting(tz, name)}</h1>
        </div>
        {allowed && <ControlBar prefs={prefs} save={save} data={data} storeName={data?.store.name || me.store?.name || "Store"} />}
      </div>

      {!allowed ? (
        <div className="tf-card rounded-[var(--k-r-md)] bg-[var(--k-surface)] px-5 py-6 text-[14px] text-[var(--k-ink-muted)]">Home shows sales, payments and what needs attention. Ask the store owner for Analytics access to see it.</div>
      ) : error ? (
        <ErrorState error={error} />
      ) : !data ? (
        <HomeSkeleton />
      ) : (
        <div className={`flex min-w-0 flex-col gap-4 transition-opacity ${isFetching && !data ? "opacity-60" : ""}`}>
          <KpiStrip data={data} tiles={prefs.tiles} chart={chart} onPick={(k) => save({ chart: k })} onEdit={() => setEditTiles(true)} />
          <Attention data={data} className="xl:hidden" />
          <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="min-w-0 xl:col-span-2">
              <PrimaryChart data={data} metric={chart} onMetric={(k) => save({ chart: k })} />
            </div>
            <Funnel data={data} />
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
            <Waterfall data={data} />
            <PaymentsHealth data={data} mid={prefs.mid} onMid={(m) => save({ mid: m })} />
          </div>
          <div className={`grid min-w-0 grid-cols-1 gap-4 ${data.subscriptions ? "xl:grid-cols-2" : ""}`}>
            <Attention data={data} className="hidden xl:flex" />
            <Subscriptions data={data} />
          </div>
          <AdCampaignsPanel ads={data.ad_campaigns} tz={data.store.timezone} />
          <Acquisition data={data} />
          <RecentOrders data={data} />
        </div>
      )}
      <TilePicker open={editTiles} onClose={() => setEditTiles(false)} data={data} tiles={prefs.tiles} onSave={(tiles) => save({ tiles })} />
    </div>
  )
}

function HomeSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading Home">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[var(--k-r-md)] md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="bg-[var(--k-sunken)] px-5 py-4">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-3 h-7 w-24" />
            <Skeleton className="mt-3 h-6 w-full" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Skeleton className="h-[340px] rounded-[var(--k-r-md)] xl:col-span-2" />
        <Skeleton className="h-[340px] rounded-[var(--k-r-md)]" />
      </div>
    </div>
  )
}
